/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionShareURL';

const ActionShareURLManager: ActionManagerFn<'ActionShareURL'> = (
  platform: Platform,
): ActionManager<'ActionShareURL'> => {
  return {
    ...baseActionManager(platform, 'ActionShareURL'),
    defaultValue,
    props: {
      ...baseActionProps,
      defaultName: {
        label: 'Default Friendly Name',
        type: 'expression',
        returnType: async () => 'string',
      },
    },
    name: 'Share URL',
    icon: { iconName: 'clipboard' },
    description: `**Overview**<br/>
The Share URL action is a social and collaboration utility component designed to facilitate the distribution of the application's content. It acts as a bridge to the user device's native sharing capabilities (such as the Web Share API on modern browsers or mobile OS share sheets), enabling users to easily send the URL of the current page to other applications, contacts, or the clipboard.<br/><br/>
This component is particularly useful in deep-linking scenarios where the current URL contains specific parameters (like a Record ID or filter state) that define the exact view the user is looking at. It simplifies the process of "Copy Link" or "Send to..." without requiring the user to manually manipulate the browser address bar.

**Key Features**<br/>
* **Native Integration:** Invokes the system-level share dialog where available, offering options like "Message," "Mail," "Copy," or specific social apps installed on the device.
* **Context Preservation:** Shares the exact current URL, including all query parameters, ensuring that the recipient sees the same data context (e.g., the specific order details) as the sender.
* **Custom Context:** Includes a defaultName property, allowing developers to attach a friendly title or description to the shared payload (e.g., sharing "Invoice #500" instead of just a raw link).

**Properties**<br/>
* **Default Friendly Name (defaultName):** An expression property returning a string.
  * *Function:* Defines the title or text associated with the shared link when the share sheet opens.
  * *Usage:* Useful for providing context to the recipient (e.g., the subject line of an email or the preview text in a messaging app).

**Use Cases**<br/>
* **Collaboration:** A "Share" button on a Project Dashboard that allows a manager to quickly slack the link to their team.
* **Deep Linking:** Sending a specific invoice URL to a client via email directly from the "Invoice Details" page.
* **Mobile Workflows:** Enabling a field technician to share the link to a specific service request with a specialist via WhatsApp or SMS.`,
  };
};

export default ActionShareURLManager;
